/**
 * @fileoverview Inline "Update Sales Stage" form for TenantDetailModal.
 * @description Only rendered for the target's current owner (enforced again, defense-in-depth, by
 * the mock API itself). 'Active Client' is excluded from the selectable stages — "Active Client
 * cannot be set through an ordinary manual stage update" — and a reason note is required for
 * 'Lost'/'On Hold'.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useUpdateTargetStage } from '@/features/sales/hooks/useUpdateTargetStage'
import {
  updateStageFormSchema,
  type UpdateStageFormValues,
} from '@/features/sales/schemas/update-stage-form.schema'
import Label from '@/shared/components/atoms/Label'
import Select from '@/shared/components/atoms/Select'

import DialogActions from './DialogActions'

import type { SalesStage } from '@/features/sales/types/tenant.types'
import type { FormEvent } from 'react'

interface UpdateSalesStageFormProps {
  tenantId: string
  targetId: string
  currentStage: SalesStage
  actingUserId: string
  onUpdated: () => void
  onCancel: () => void
}

const SELECTABLE_STAGES: UpdateStageFormValues['stage'][] = [
  'Prospect',
  'Contacted',
  'Meeting/Demo',
  'Proposal',
  'Negotiation',
  'Awaiting Payment',
  'On Hold',
  'Lost',
]
const STAGE_KEY: Record<UpdateStageFormValues['stage'], string> = {
  Prospect: 'prospect',
  Contacted: 'contacted',
  'Meeting/Demo': 'meetingDemo',
  Proposal: 'proposal',
  Negotiation: 'negotiation',
  'Awaiting Payment': 'awaitingPayment',
  'On Hold': 'onHold',
  Lost: 'lost',
}

/**
 * @component
 * @param {UpdateSalesStageFormProps} props - The target to update and the acting owner's id.
 * @param {string} props.tenantId - The tenant the target belongs to, for query invalidation.
 * @param {string} props.targetId - The target id to update.
 * @param {SalesStage} props.currentStage - The target's current stage, as the form's default.
 * @param {string} props.actingUserId - The signed-in owner's sales-roster id.
 * @param {() => void} props.onUpdated - Called after a successful update.
 * @param {() => void} props.onCancel - Called to dismiss the form without updating.
 */
function UpdateSalesStageForm({
  tenantId,
  targetId,
  currentStage,
  actingUserId,
  onUpdated,
  onCancel,
}: UpdateSalesStageFormProps) {
  const { t } = useTranslation()
  const updateStage = useUpdateTargetStage(tenantId)
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<UpdateStageFormValues>({
    resolver: zodResolver(updateStageFormSchema),
    defaultValues: {
      stage: SELECTABLE_STAGES.includes(currentStage as UpdateStageFormValues['stage'])
        ? (currentStage as UpdateStageFormValues['stage'])
        : 'Prospect',
      reason: '',
    },
  })
  const stage = useWatch({ control, name: 'stage' })
  const requiresReason = stage === 'Lost' || stage === 'On Hold'

  /**
   * @description Submits the stage update.
   * @param {UpdateStageFormValues} values - The validated form values.
   */
  const onValid = (values: UpdateStageFormValues) => {
    updateStage.mutate(
      {
        targetId,
        input: {
          stage: values.stage,
          actingUserId,
          ...(values.reason ? { reason: values.reason } : {}),
        },
      },
      { onSuccess: onUpdated },
    )
  }

  /**
   * @description Runs the schema validation and, if valid, calls onValid.
   * @param {FormEvent<HTMLFormElement>} event - The form submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      data-testid="update-stage-form"
      className="flex flex-col gap-3 rounded-lg border border-row-border bg-surface-hover p-3.5"
    >
      <span className="text-[12.5px] font-bold text-ink" data-testid="update-stage-title">
        {t('sales.tenantDetail.stage.title')}
      </span>

      <div>
        <Label htmlFor="newStage">{t('sales.tenantDetail.stage.label')}</Label>
        <div className="mt-1.5">
          <Select
            id="newStage"
            data-testid="update-stage-select"
            options={SELECTABLE_STAGES.map((value) => ({
              value,
              label: t(`sales.tenantDetail.stage.stages.${STAGE_KEY[value]}`),
            }))}
            {...register('stage')}
          />
        </div>
      </div>

      {requiresReason && (
        <div>
          <Label htmlFor="stageReason">{t('sales.tenantDetail.stage.reasonLabel')}</Label>
          <textarea
            id="stageReason"
            data-testid="update-stage-reason-input"
            rows={2}
            placeholder={t('sales.tenantDetail.stage.reasonPlaceholder')}
            className="mt-1.5 w-full rounded-lg border border-input-border bg-white px-3 py-2 text-[13.5px] text-ink focus:outline-none focus:ring-2 focus:ring-primary/20"
            {...register('reason')}
          />
          {errors.reason && (
            <span
              className="mt-1 block text-[11.5px] font-medium text-danger"
              data-testid="update-stage-reason-error"
            >
              {t(errors.reason.message ?? '')}
            </span>
          )}
        </div>
      )}

      <DialogActions
        testIdPrefix="update-stage"
        cancelLabel={t('sales.pipeline.form.cancel')}
        confirmLabel={t('sales.tenantDetail.stage.submit')}
        onCancel={onCancel}
        confirmType="submit"
        isLoading={updateStage.isPending}
        disabled={false}
      />

      {updateStage.isError && (
        <p role="alert" className="text-[12.5px] text-danger" data-testid="update-stage-error">
          {updateStage.error instanceof Error
            ? updateStage.error.message
            : String(updateStage.error)}
        </p>
      )}
    </form>
  )
}

export default UpdateSalesStageForm
